// src/HymnDisplay.jsx
import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import { doc, getDoc, collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "./firebaseConfig";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import TextDecreaseIcon from "@mui/icons-material/TextDecrease";
import TextIncreaseIcon from "@mui/icons-material/TextIncrease";
import { Link as RouterLink, useParams, useNavigate } from "react-router-dom";
import { ensureStructuredFromDoc } from "./utils/lyrics";

export default function HymnDisplay() {
  const { hymnId } = useParams();
  const navigate = useNavigate();

  const [hymn, setHymn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dark, setDark] = useState(false);
  const [fontSize, setFontSize] = useState(18);

  const [prevDoc, setPrevDoc] = useState(null);
  const [nextDoc, setNextDoc] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const ref = doc(db, "hymns", hymnId);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
          setHymn(null);
          setLoading(false);
          return;
        }
        setHymn({ id: snap.id, ...snap.data() });
      } catch (e) {
        console.error("Load hymn error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [hymnId]);

  useEffect(() => {
    if (!hymn?.master_number) return;
    (async () => {
      const m = hymn.master_number;

      const prevQ = query(
        collection(db, "hymns"),
        where("master_number", "<", Number(m)),
        orderBy("master_number", "desc"),
        limit(1)
      );
      const nextQ = query(
        collection(db, "hymns"),
        where("master_number", ">", Number(m)),
        orderBy("master_number", "asc"),
        limit(1)
      );

      const [prevSnap, nextSnap] = await Promise.all([getDocs(prevQ), getDocs(nextQ)]);
      setPrevDoc(!prevSnap.empty ? { id: prevSnap.docs[0].id, ...prevSnap.docs[0].data() } : null);
      setNextDoc(!nextSnap.empty ? { id: nextSnap.docs[0].id, ...nextSnap.docs[0].data() } : null);
    })();
  }, [hymn?.master_number]);

  const structured = useMemo(() => {
    if (!hymn) return { stanzas: [], refrain: [] };
    return ensureStructuredFromDoc(hymn);
  }, [hymn]);

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading hymn…</Typography>
      </Box>
    );
  }

  if (!hymn) {
    return (
      <Box sx={{ p: 6, textAlign: "center" }}>
        <Typography variant="h6">Hymn not found</Typography>
        <Button component={RouterLink} to="/" sx={{ mt: 2 }} variant="outlined">
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: dark ? "#111" : "#f9f9f9",
        color: dark ? "#f9f9f9" : "#111",
        minHeight: "100vh",
        py: 3,
      }}
    >
      {/* Top Bar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, mb: 2 }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          component={RouterLink}
          to="/"
          variant={dark ? "outlined" : "contained"}
        >
          Back
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Smaller text">
            <IconButton onClick={() => setFontSize((s) => Math.max(12, s - 2))}>
              <TextDecreaseIcon />
            </IconButton>
          </Tooltip>
          <Typography>{fontSize}px</Typography>
          <Tooltip title="Larger text">
            <IconButton onClick={() => setFontSize((s) => Math.min(40, s + 2))}>
              <TextIncreaseIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={dark ? "Light mode" : "Dark mode"}>
            <IconButton onClick={() => setDark((d) => !d)}>
              {dark ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Hymn Content */}
      <Paper
        sx={{
          maxWidth: 800,
          mx: "auto",
          p: { xs: 2, md: 3 },
          bgcolor: dark ? "#181818" : "#fff",
          borderRadius: 2,
        }}
      >
        {/* Hymn Heading */}
        <Typography align="center" variant="h6" sx={{ fontWeight: 700 }}>
          {hymn.title}
        </Typography>

        <Typography align="center" sx={{ opacity: 0.8, mt: 0.5 }}>
          {hymn.author_name ? `By; ${hymn.author_name}` : ""}
          {hymn.author_name && hymn.key_label ? " • " : ""}
          {hymn.key_label ? `Key: ${hymn.key_label}` : ""}
        </Typography>

        <Typography align="center" variant="body2" sx={{ mt: 0.5, opacity: 0.7 }}>
          Hymn #{hymn.master_number}
        </Typography>

        <Divider sx={{ my: 2 }} />

        {/* Hymn Text */}
        <Box sx={{ fontSize, lineHeight: 1.6 }}>
          {structured.stanzas && structured.stanzas.length > 0 ? (
            structured.stanzas.map((stanza, idx) => (
              <Box key={idx} sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>
                  Stanza {idx + 1}
                </Typography>
                <Box sx={{ whiteSpace: "pre-line" }}>
                  {stanza.join("\n")}
                </Box>

                {/* Show Chorus after stanza 1 */}
                {idx === 0 && structured.refrain && structured.refrain.length > 0 && (
                  <Box sx={{ mt: 2, pl: 2, borderLeft: "3px solid #999" }}>
                    <Typography variant="subtitle2" sx={{ opacity: 0.7 }}>
                      Chorus
                    </Typography>
                    <Box sx={{ whiteSpace: "pre-line" }}>
                      {structured.refrain.join("\n")}
                    </Box>
                  </Box>
                )}
              </Box>
            ))
          ) : (
            <Typography sx={{ whiteSpace: "pre-line" }}>{hymn.lyrics}</Typography>
          )}
        </Box>

        {/* Navigation */}
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
          <Button
            startIcon={<NavigateBeforeIcon />}
            disabled={!prevDoc}
            onClick={() => prevDoc && navigate(`/view/${prevDoc.id}`)}
          >
            Previous
          </Button>
          <Button
            endIcon={<NavigateNextIcon />}
            disabled={!nextDoc}
            onClick={() => nextDoc && navigate(`/view/${nextDoc.id}`)}
          >
            Next
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
